import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import useAudioContext from "../../../hooks/useAudioContext";
import useSpeechRecognition from "../../../hooks/useSpeechRecognition";
import useTTS from "../../../hooks/useTTS";

export const VOICE_ORB_STATES = ["idle", "listening", "processing", "speaking"] as const;

export type VoiceOrbState = (typeof VOICE_ORB_STATES)[number];

export const VOICE_LOCALES = {
  es: "es-MX",
  en: "en-US",
} as const satisfies Record<string, string>;

const DEFAULT_VOICE_LOCALE = VOICE_LOCALES.es;

export const voiceLocaleOf = (language: string): string =>
  VOICE_LOCALES[language.split("-")[0] as keyof typeof VOICE_LOCALES] ?? DEFAULT_VOICE_LOCALE;

export interface UseCentinelaVoiceReturn {
  orbState: VoiceOrbState;
  analyserNode: AnalyserNode | null;
  interimTranscript: string;
  isListening: boolean;
  isSupported: boolean;
  error: string | null;
  voiceEnabled: boolean;
  isSpeaking: boolean;
  speakingId: string | null;
  toggleVoiceOutput: () => void;
  speak: (text: string, id?: string) => void;
  stopSpeaking: () => void;
  toggleListening: () => void;
  closeOrb: () => void;
  release: () => void;
}

export function useCentinelaVoice(
  onTranscript: (text: string) => void,
): UseCentinelaVoiceReturn {
  const { i18n } = useTranslation();
  const locale = voiceLocaleOf(i18n.language);

  const [phase, setPhase] = useState<Exclude<VoiceOrbState, "speaking">>("idle");
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);

  const releaseMic = useCallback(() => {
    micStreamRef.current?.getTracks().forEach((track) => track.stop());
    micStreamRef.current = null;
    setAnalyserNode(null);
  }, []);

  const handleAutoEnd = useCallback(
    (transcript: string) => {
      releaseMic();
      setPhase("idle");

      const value = transcript.trim();
      value && onTranscript(value);
    },
    [onTranscript, releaseMic],
  );

  const audio = useAudioContext();
  const speech = useSpeechRecognition(locale, handleAutoEnd);
  const tts = useTTS(locale);

  useEffect(() => {
    !tts.isSpeaking && setSpeakingId(null);
  }, [tts.isSpeaking]);

  const speak = useCallback(
    (text: string, id?: string) => {
      setSpeakingId(id ?? null);
      tts.speak(text);
    },
    [tts],
  );

  const stopSpeaking = useCallback(() => {
    tts.stop();
    setSpeakingId(null);
  }, [tts]);

  const startListening = useCallback(async () => {
    await audio.unlock();
    setMicError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const context = audio.getContext();
      if (context) {
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        setAnalyserNode(analyser);
      }
    } catch (error) {
      releaseMic();
      setPhase("idle");
      setMicError(error instanceof Error ? error.name : "mic-unavailable");
      return;
    }

    setPhase("listening");
    speech.startListening();
  }, [audio, releaseMic, speech]);

  const stopListening = useCallback(async () => {
    setPhase("processing");
    const transcript = await speech.stopListening();
    releaseMic();
    setPhase("idle");

    const value = transcript.trim();
    value && onTranscript(value);
  }, [onTranscript, releaseMic, speech]);

  const toggleListening = useCallback(() => {
    speech.isListening ? void stopListening() : void startListening();
  }, [speech.isListening, startListening, stopListening]);

  const closeOrb = useCallback(() => {
    speech.isListening && void speech.stopListening();
    releaseMic();
    setPhase("idle");
  }, [releaseMic, speech]);

  const release = useCallback(() => {
    tts.stop();
    closeOrb();
  }, [closeOrb, tts]);

  return {
    orbState: phase,
    analyserNode,
    interimTranscript: speech.interimTranscript,
    isListening: speech.isListening,
    isSupported: speech.isSupported,
    error: micError ?? speech.error,
    voiceEnabled: tts.voiceEnabled,
    isSpeaking: tts.isSpeaking,
    speakingId,
    toggleVoiceOutput: tts.toggleVoice,
    speak,
    stopSpeaking,
    toggleListening,
    closeOrb,
    release,
  };
}

export default useCentinelaVoice;
